import { Dispatch, LegacyRef, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';

import { useGetFilterOptionsQuery } from 'app/api/facade';
import { useAppSelector } from 'app/store/store';
import { useGetAllValue } from 'app/hooks/useGetAllValue';
import { SingleFilterValue } from 'ais-facade-api/api';

import { Icon, Button, Input, CheckboxOption, CheckboxAll } from 'app/components';

import { Filters } from 'app/types/types';
import { Filters as VesselsCatalogFilters } from 'app/pages/VesselsPage/VesselsPage';

import styles from './FilterPanel.module.scss';
import clsx from 'clsx';
interface Props {
  isOpen?: boolean;
  onClose: () => void;
  innerRef?: LegacyRef<HTMLDivElement>;
  filterStateControl: [Filters | VesselsCatalogFilters | null, Dispatch<SetStateAction<Filters | VesselsCatalogFilters | null>>];
}

export interface FilterFormData {
  touched: boolean;
  fleetOnly: boolean;
  source: string[];
  status: string[];
  country: string[];
  type: string[];
  fleetGroup: string[];
  statusAll: boolean;
  typeAll: boolean;
  countryAll: boolean;
  sourceAll: boolean;
  fleetGroupAll: boolean;
}

export const AtonsFilterPanel = ({ onClose, innerRef, filterStateControl }: Props) => {
  const { t } = useTranslation();

  const { data, isError, error, ...filterOptionsRequest } = useGetFilterOptionsQuery();
  const uiConfig = useAppSelector((state) => state.userSlice.uiConfig);

  const allStatuses = useGetAllValue(data?.filter?.status?.values);
  const allSources = useGetAllValue(data?.filter?.source?.values);
  const allTypes = useGetAllValue(data?.filter?.type?.values);
  const allCountries = useGetAllValue(data?.filter?.country?.values);
  const allFleetGroups = useGetAllValue(data?.filter?.fleetGroup?.values);

  const [countryList, setContryList] = useState(data?.filter?.country?.values);
  const [inputValue, setInputValue] = useState<string>('');
  const [filters, setFilters] = filterStateControl || [];

  useEffect(() => {
    filterOptionsRequest.refetch();
  }, []);

  useEffect(() => {
    if (data?.filter?.country?.values) {
      setContryList(data?.filter?.country?.values);
    }
  }, [data]);

  const initialValues = useMemo(() => {
    return {
      touched: false,
      fleetOnly: uiConfig?.viewportFleetOnly || false,
      source: allSources,
      status: allStatuses,
      type: allTypes,
      country: allCountries,
      fleetGroup: allFleetGroups,
      statusAll: true,
      typeAll: true,
      countryAll: true,
      sourceAll: true,
      fleetGroupAll: true,
    };
  }, [allCountries, allSources, allTypes, allStatuses, allFleetGroups, uiConfig]);

  const initialFormValue = useMemo(() => {
    if (filters && Object.keys(filters).length > 0) {
      return {
        touched: filters.touched || false,
        fleetOnly: filters.fleetOnly || uiConfig?.viewportFleetOnly || false,
        source: filters.source?.map((e) => String(e)) || [],
        type: filters.type?.map((e) => String(e)) || [],
        country: filters.country?.map((e) => String(e)) || [],
        status: filters.status?.map((e) => String(e)) || [],
        fleetGroup: filters.fleetGroup?.map((e) => String(e)) || [],
        statusAll: filters.statusAll,
        typeAll: filters.typeAll,
        countryAll: filters.countryAll,
        sourceAll: filters.sourceAll,
        fleetGroupAll: filters.fleetGroupAll,
      };
    }

    return {
      touched: false,
      fleetOnly: typeof uiConfig?.viewportFleetOnly === 'boolean' ? uiConfig?.viewportFleetOnly : false,
      source: allSources || [],
      status: allStatuses || [],
      type: allTypes || [],
      country: allCountries || [],
      fleetGroup: allFleetGroups || [],
      statusAll: true,
      typeAll: true,
      countryAll: true,
      sourceAll: true,
      fleetGroupAll: true,
    };
  }, [filters, allCountries, allSources, allTypes, allStatuses, allFleetGroups, uiConfig]);

  const handleSubmit = useCallback(
    (formData: FilterFormData) => {
      const { touched: initialTouched, ...initialValuesRaw } = initialValues;
      const { touched: formTouched, ...formDataRaw } = formData;

      const isEq =
        formDataRaw.countryAll === initialValuesRaw.countryAll &&
        formDataRaw.fleetGroupAll === initialValuesRaw.fleetGroupAll &&
        formDataRaw.sourceAll === initialValuesRaw.sourceAll &&
        formDataRaw.statusAll === initialValuesRaw.statusAll &&
        formDataRaw.fleetOnly === initialValuesRaw.fleetOnly &&
        formDataRaw.typeAll === initialValuesRaw.typeAll;

      const { fleetOnly, source, type, country, status, fleetGroup, statusAll, typeAll, countryAll, sourceAll, fleetGroupAll } =
        formData;

      const filterData = {
        touched: !isEq,
        fleetOnly,
        source: source.map((e) => Number(e)),
        type: type.map((e) => Number(e)),
        country: country.map((e) => Number(e)),
        status: status.map((e) => Number(e)),
        fleetGroup,
        statusAll,
        typeAll,
        countryAll,
        sourceAll,
        fleetGroupAll,
      };
      setFilters(filterData);
      onClose();
    },
    [initialFormValue, filters],
  );

  const formik = useFormik<FilterFormData>({
    initialValues: initialFormValue,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const handleReset = useCallback(() => {
    formik.setValues({
      touched: false,
      fleetOnly: typeof uiConfig?.viewportFleetOnly === 'boolean' ? uiConfig?.viewportFleetOnly : false,
      source: allSources || [],
      status: allStatuses || [],
      type: allTypes || [],
      country: allCountries || [],
      fleetGroup: allFleetGroups || [],
      statusAll: true,
      typeAll: true,
      countryAll: true,
      sourceAll: true,
      fleetGroupAll: true,
    });
  }, [allSources, allStatuses, allTypes, allCountries, allFleetGroups, uiConfig]);

  const sourceGroup = useMemo(() => {
    return data?.filter?.source?.values?.map((value) => {
      return (
        <CheckboxOption<FilterFormData>
          key={value.name}
          value={value}
          name="source"
          formValues={formik.values}
          formSetField={formik.setFieldValue}
        />
      );
    });
  }, [data, formik]);

  const checkboxGroupFleetGroup = useMemo(() => {
    return data?.filter?.fleetGroup?.values?.map((fleetGroup) => {
      return (
        <CheckboxOption<FilterFormData>
          disabled={!formik.values.fleetOnly}
          key={fleetGroup.key}
          value={fleetGroup}
          name="fleetGroup"
          formValues={formik.values}
          formSetField={formik.setFieldValue}
        />
      );
    });
  }, [data, allFleetGroups, formik]);

  const checkboxGroupStatus = useMemo(() => {
    return data?.filter?.status?.values?.map((statusValue) => {
      return (
        <CheckboxOption<FilterFormData>
          key={statusValue.name}
          value={statusValue}
          name="status"
          formValues={formik.values}
          formSetField={formik.setFieldValue}
        />
      );
    });
  }, [data, allStatuses, formik]);

  const checkboxGroupType = useMemo(() => {
    return data?.filter?.type?.values?.map((typeValue) => {
      return (
        <CheckboxOption<FilterFormData>
          key={typeValue.name}
          value={typeValue}
          name="type"
          formValues={formik.values}
          formSetField={formik.setFieldValue}
        />
      );
    });
  }, [data, allTypes, formik]);

  const checkboxGroupCountry = useMemo(() => {
    if (formik.values.countryAll === false) {
      const checkedItems: SingleFilterValue[] = [];
      let uncheckedItems: SingleFilterValue[] = [];
      const cl = countryList || [];
      let common: SingleFilterValue[] = [];

      if (cl) {
        for (let i = 0; i < cl.length; i++) {
          for (let j = 0; j < formik.values.country.length; j++) {
            if (cl[i].values?.[0] === Number(formik.values.country[j])) {
              checkedItems.push(cl[i]);
            }
          }
        }
      }

      uncheckedItems = cl.filter((o) => checkedItems.indexOf(o) === -1);
      common = checkedItems.concat(uncheckedItems);

      return common?.map((countryValue) => {
        return (
          <CheckboxOption<FilterFormData>
            key={countryValue.name}
            value={countryValue}
            name="country"
            formValues={formik.values}
            formSetField={formik.setFieldValue}
          />
        );
      });
    } else {
      return countryList?.map((countryValue) => {
        return (
          <CheckboxOption<FilterFormData>
            key={countryValue.name}
            value={countryValue}
            name="country"
            formValues={formik.values}
            formSetField={formik.setFieldValue}
          />
        );
      });
    }
  }, [data, allCountries, formik, countryList]);

  if (isError && 'originalStatus' in error && error.originalStatus === 400) {
    filterOptionsRequest.refetch();
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    const inputValue: string = e.target.value.toLowerCase();

    const filtered = data?.filter?.country?.values?.filter((value) => value?.name?.toLowerCase().includes(inputValue));
    setContryList(filtered);
  };

  return (
    <div className={styles.root}>
      <div className={styles.dialog_modal} ref={innerRef}>
        <div className={styles.panel_header}>
          <p className={styles.heading}>{t('FilterPanelHeading')}</p>
          <div onClick={onClose} className={styles.close_button}>
            <Icon size="16" name="close-16" />
          </div>
        </div>
        <div className={styles.form_container_atons}>
          {!data && <div className={styles.loader} />}
          {data && (
            <form onSubmit={formik.handleSubmit} className={styles.form}>
              <div className={styles.container_atons}>
                <div className={styles.checkboxGroup}>
                  <div className={styles.filter_container}>
                    <label className={styles.groupName} htmlFor="source">
                      {`${t('DataSourceLabel')}:`}
                    </label>
                    {sourceGroup}
                  </div>

                  <div className={styles.filter_container}>
                    <label className={styles.groupName} htmlFor="fleetGroup">
                      {`${t('FilterPanelLabelFleetGroup')}:`}
                    </label>

                    <label className={styles.checkboxLabel} aria-disabled={uiConfig?.viewportFleetOnly}>
                      <input
                        id="fleetOnly"
                        type="checkbox"
                        name="fleetOnly"
                        className={styles.checkboxInput}
                        onChange={formik.handleChange}
                        checked={formik.values.fleetOnly}
                        disabled={uiConfig?.viewportFleetOnly}
                      />
                      {t('FilterPanelLabelFleetOnly')}
                    </label>
                    {/* TODO: Вернуть при добавлении групп для атонов */}
                    {/*<CheckboxAll<FilterFormData>*/}
                    {/*  disabled={!formik.values.fleetOnly}*/}
                    {/*  name="fleetGroup"*/}
                    {/*  allValues={allFleetGroups}*/}
                    {/*  formValues={formik.values}*/}
                    {/*  formHandleChange={formik.handleChange}*/}
                    {/*  formSetField={formik.setFieldValue}*/}
                    {/*/>*/}
                    {/*<div className={styles.listGroup}>{checkboxGroupFleetGroup}</div>*/}
                  </div>
                </div>

                <div className={styles.checkboxGroup}>
                  <label className={styles.groupName} htmlFor="status">
                    {`${t('FilterPanelLabelStatus')}:`}
                  </label>

                  <CheckboxAll<FilterFormData>
                    name="status"
                    allValues={allStatuses}
                    formValues={formik.values}
                    formHandleChange={formik.handleChange}
                    formSetField={formik.setFieldValue}
                  />
                  <div className={styles.listGroup}>{checkboxGroupStatus}</div>
                </div>

                <div className={clsx(styles.checkboxGroup, styles.countryFilter)}>
                  <label className={styles.groupName} htmlFor="country">
                    {`${t('FilterPanelLabelCountries')}:`}
                  </label>

                  <CheckboxAll<FilterFormData>
                    name="country"
                    allValues={allCountries}
                    formValues={formik.values}
                    formHandleChange={formik.handleChange}
                    formSetField={formik.setFieldValue}
                  />
                  <Input
                    type="search"
                    onChange={handleInputChange}
                    placeholder={t('SearchPlaceholder')}
                    className={styles.input}
                    value={inputValue}
                  />
                  <div className={styles.listGroup}>{checkboxGroupCountry}</div>
                </div>
              </div>

              <div className={styles.form_actions}>
                <Button
                  className={styles.action_button}
                  type="button"
                  label={t('FilterPanelButtonReset')}
                  variant="general"
                  onClick={handleReset}
                />
                <div>
                  <Button className={styles.action_button} label={t('FilterPanelButtonSubmit')} variant="general" type="submit" />
                  <Button
                    className={styles.action_button}
                    type="button"
                    label={t('ButtonLabelDiscard')}
                    variant="general"
                    onClick={onClose}
                  />
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
